import { Transition } from '@headlessui/react';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { deleteData, OAuthError, patchData, postData, putData } from "../backend";
import { useModal, WorkflowContext } from "../contexts";
import useUser from "../hooks/useUser";
import { Attachment, Email, AbstractEmail } from "../interfaces";
import EmailEditor, { AttachmentAction, Ref as EmailEdtorRef } from './EmailEditor';
import OAuthModal from './OAuthModal';
import OAuthModalMicrosoft from './OAuthModalMicrosoft';
import TransitionWorkflowPage from './transitions/TransitionWorkflowPage';
import WorkflowEmailSidebar from './WorkflowEmailSidebar';

function WorkflowEmail() {
  const { t } = useTranslation();
  const { hasPermission } = useUser();

  const { openModal } = useModal();

  const history = useHistory();
  const { url } = useRouteMatch();

  const location = useLocation();

  const workflowContext = useContext(WorkflowContext)
  const { workflow, mutateWorkflow } = workflowContext;

  const [email, setEmail] = useState<Email>()

  const [hasCheckedScopes, setHasCheckedScopes] = useState(false);

  const allowEditing = hasPermission(workflow.team, "workflows.edit");
  const emailEditorRef = useRef<EmailEdtorRef>(null);

  const index = (() => {
    const hash = location.hash.split("#")[1];
    return (parseInt(hash) || 1) - 1;
  })();

  const initialEmail = workflow.emails[index] ?? workflow.emails[0];

  useLayoutEffect(() => {
    if (!(initialEmail && initialEmail.id !== email?.id))
      return;

    setEmail(initialEmail);
  }, [initialEmail, email, emailEditorRef]);

  useEffect(() => {
    if (hasCheckedScopes || !initialEmail || !location.pathname.endsWith("/email"))
      return;

    if (initialEmail.emailType === "GMAIL")
      postData("/auth/scopes/", { scopes: ["https://www.googleapis.com/auth/gmail.send"] })
        .catch(error => {
          if (error instanceof OAuthError)
            openModal(<OAuthModal title={t('Portant requires your permission to send emails')} oauthUrl={error.oauthUrl} />);
        })
        .finally(() => setHasCheckedScopes(true));

    else if (initialEmail.emailType === "OUTLOOK")
      postData("/microsoft/access-token/", {})
        .catch(error => {
          if (error instanceof OAuthError)
            openModal(<OAuthModalMicrosoft oauthUrl={error.oauthUrl} />);
        })
        .finally(() => setHasCheckedScopes(true));

  }, [hasCheckedScopes, openModal, initialEmail, location, t]);

  async function updateAttachment(action: AttachmentAction, attachmentId: string, attachment?: Partial<Attachment>) {
    if (action === "CREATE")
      return postData(`/workflows/emails/${initialEmail.id}/attachments/`, attachment);

    else if (action === "DELETE")
      return deleteData(`/workflows/attachments/${attachmentId}/`);

    else if (action === "UPDATE")
      return patchData(`/workflows/attachments/${attachmentId}/`, { sharingMode: attachment!.sharingMode });
  }

  async function updateEmail(email: AbstractEmail) {
    const updatedEmail = await putData(`/workflows/emails/${initialEmail.id}/`, email);

    setEmail(updatedEmail);
    mutateWorkflow({ ...workflow, emails: workflow.emails.map(e => e.id === initialEmail.id ? updatedEmail : e) });

    return updatedEmail;
  }

  if (!initialEmail || !email)
    return null;

  return (
    <TransitionWorkflowPage show={location.pathname.endsWith("/email")}>

      {/* TODO: flatten */}

      {allowEditing &&
        <WorkflowEmailSidebar email={email} setEmail={setEmail} toggleAttachmentMenu={() => emailEditorRef.current?.openAttachmentMenu()} />
      }

      <Transition.Child className="flex w-full h-full"
        enter="transition-opacity duration-150"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <EmailEditor ref={emailEditorRef} initialEmail={email} updateEmail={updateEmail} updateAttachment={updateAttachment} onClose={() => history.push(url)} />
      </Transition.Child>
    </TransitionWorkflowPage>
  );

}

export default WorkflowEmail;
